import React, { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { HERO_WAFFLE, buildWhatsAppLink } from "../data/menu";

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rotateY = useSpring(useTransform(mx, [-1, 1], [-18, 18]), {
    stiffness: 80,
    damping: 14,
  });
  const rotateX = useSpring(useTransform(my, [-1, 1], [14, -14]), {
    stiffness: 80,
    damping: 14,
  });

  useEffect(() => {
    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx.set((e.clientX / w) * 2 - 1);
      my.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-32 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-20 -left-20 w-[520px] h-[520px] rounded-full bg-[#F7E9A8]/60 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-32 w-[460px] h-[460px] rounded-full bg-[#E6EDE5]/70 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT: copy */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#FFFDF9] border border-[#3E2723]/10 px-4 py-2 text-xs font-bold tracking-wider uppercase text-[#3E2723]/80 shadow-sm"
            data-testid="hero-eyebrow"
          >
            <Star size={14} className="text-[#D38C41] fill-[#D38C41]" />
            Premium Waffle Café · Colombia
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-display text-[#3E2723] font-black mt-6 leading-[0.92] tracking-tight"
            style={{ fontSize: "clamp(3.5rem, 12vw, 9rem)" }}
            data-testid="hero-title"
          >
            Buffy<span className="text-[#D38C41]">&rsquo;</span>s
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-[#5D4037] font-body leading-relaxed"
            data-testid="hero-subtitle"
          >
            Waffles belgas crujientes, ingredientes premium y combinaciones
            irresistibles. Dulce, salado y todo lo que se te antoje{" "}
            <span className="font-bold text-[#3E2723]">recién hecho.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="#menu"
              data-testid="hero-cta-menu"
              className="inline-flex items-center gap-2 rounded-full bg-[#3E2723] text-[#FFF0F3] px-7 py-4 font-bold buffy-link shadow-[0_20px_45px_rgba(62,39,35,0.28)] hover:shadow-[0_25px_55px_rgba(62,39,35,0.38)]"
            >
              Ver el menú
              <ArrowDown size={18} />
            </a>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-[#FFFDF9] border border-[#3E2723]/15 text-[#3E2723] px-7 py-4 font-bold buffy-link hover:bg-[#FDF8E1]"
            >
              Pedir por WhatsApp
            </a>
          </motion.div>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-12 grid grid-cols-3 gap-4 max-w-md"
            data-testid="hero-stats"
          >
            {[
              { k: "6", v: "Waffles únicos" },
              { k: "100%", v: "Hecho a mano" },
              { k: "+5⭐", v: "Reseñas" },
            ].map((s) => (
              <div key={s.v} className="">
                <div className="font-display font-black text-3xl text-[#3E2723]">
                  {s.k}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-[#5D4037] font-bold">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT: 3D floating waffle */}
        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ rotateX, rotateY, transformPerspective: 1200 }}
            className="relative mx-auto w-[min(92vw,560px)] aspect-square"
            data-testid="hero-3d-waffle"
          >
            {/* Soft pink halo */}
            <div className="absolute inset-6 rounded-full bg-gradient-to-br from-[#F7E9A8] via-[#FFE3E8] to-[#E6EDE5] blur-2xl opacity-80" />

            {/* Rotating dotted ring */}
            <div className="absolute inset-0 animate-spin-slow">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <defs>
                  <path
                    id="circle"
                    d="M 100 100 m -88, 0 a 88,88 0 1,1 176,0 a 88,88 0 1,1 -176,0"
                  />
                </defs>
                <text className="fill-[#3E2723]" fontSize="9" letterSpacing="6">
                  <textPath href="#circle">
                    • PREMIUM WAFFLES • BUFFY&rsquo;S CAFÉ • FRESH &amp; CRISPY
                    • HECHO CON AMOR
                  </textPath>
                </text>
              </svg>
            </div>

            {/* Floating waffle img - in a soft circular plate */}
            <div className="absolute inset-0 grid place-items-center">
              <div className="animate-float-3d w-[78%] aspect-square rounded-full overflow-hidden shadow-[0_40px_60px_rgba(62,39,35,0.35)] ring-8 ring-[#FFFDF9]/60 ring-offset-4 ring-offset-transparent">
                <img
                  src={HERO_WAFFLE}
                  alt="Waffle premium Buffy's"
                  className="w-full h-full object-cover select-none"
                  draggable="false"
                />
              </div>
            </div>

            {/* Floating price chips */}
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, type: "spring" }}
              className="absolute -left-2 top-10 bg-[#FFFDF9] rounded-2xl px-4 py-3 shadow-[0_15px_30px_rgba(62,39,35,0.12)] border border-[#3E2723]/10"
              data-testid="hero-chip-1"
            >
              <div className="text-[10px] uppercase tracking-wider text-[#5D4037] font-bold">
                Desde
              </div>
              <div className="font-display font-black text-xl text-[#3E2723]">
                $10.000
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.1, type: "spring" }}
              className="absolute -right-2 bottom-12 bg-[#3E2723] text-[#FFF0F3] rounded-2xl px-4 py-3 shadow-[0_15px_30px_rgba(62,39,35,0.3)]"
              data-testid="hero-chip-2"
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">
                Nuevo
              </div>
              <div className="font-display font-black text-base">
                Maní + Almendras
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
